import { Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { AccountBalanceService, WalletItem, WalletType } from '@frontend/sports/account-balance/feature';
import { IsQuickDepositAllowedResult, QuickDepositConfig } from '@frontend/sports/common/client-config-data-access';
import { fromSportsLeave } from '@frontend/sports/host-app/sports-product/feature/utils';
import { UserService } from '@frontend/sports/user/feature';
import { AppInfoConfig, Logger, NativeAppService, Page } from '@frontend/vanilla/core';
import { Store } from '@ngrx/store';
import { BetslipQdMessageService } from '@payments/betslipqd-feature';
import { BehaviorSubject, Observable, Subject, catchError, first, map, of } from 'rxjs';

import { QuickDepositActions } from '../quick-deposit-actions';
import {
    CashierEventType,
    CashierPage,
    CashierWindowEvent,
    DepositAvailable,
    FundsAdded,
    PaymentMethod,
    QuickDepositPage,
    SelectedPaymentMethod,
    WindowResize,
    WindowScroll,
} from '../quick-deposit.models';

@Injectable()
export class QuickDepositService {
    get isEnabled(): boolean {
        return (
            this.userService.isAuthenticated &&
            (this.quickDepositConfig.loadQuickDepositConfigOnLogin
                ? this.userService.isQuickDepositEnabled
                : this.quickDepositAllowedResult.isQuickDepositAllowed) &&
            this.quickDepositConfig.isQuickDepositEnabled
        );
    }

    isOpen = false;
    isAvailable = false;
    keepOpen = false;

    private stake: number;
    private cashierEvents = new Subject<CashierWindowEvent>();
    private cashierWindow: Window;
    private isQuickDepositLoaderEnabledSubject$: BehaviorSubject<boolean>;

    constructor(
        private page: Page,
        private quickDepositConfig: QuickDepositConfig,
        private userService: UserService,
        private appInfoConfig: AppInfoConfig,
        private accountBalanceService: AccountBalanceService,
        private logger: Logger,
        private store: Store,
        private quickDepositAllowedResult: IsQuickDepositAllowedResult,
        private nativeAppService: NativeAppService,
        private betslipQdMessageService: BetslipQdMessageService,
    ) {
        this.isQuickDepositLoaderEnabledSubject$ = new BehaviorSubject<boolean>(false);

        fromSportsLeave()
            .pipe(takeUntilDestroyed())
            .subscribe(() => this.disableQuickDepositLoader());
    }

    open(stake: number): void {
        if (!this.isOpen) {
            this.stake = stake;
            this.isOpen = true;
            this.store.dispatch(QuickDepositActions.quickDepositOpen());
        }
    }

    close(withAnimation: boolean): void {
        if (this.isOpen) {
            this.isOpen = false;
            this.isAvailable = false;
            this.store.dispatch(QuickDepositActions.quickDepositClose({ close: withAnimation }));
        }
    }

    deposit(): Observable<FundsAdded> {
        if (this.quickDepositConfig.isPaymentsLibraryEnabled) {
            this.betslipQdMessageService.sendInput({ eventName: 'Deposit' });
        } else {
            this.cashierWindow?.postMessage({ eventName: 'Deposit' }, '*');
        }

        return this.cashierEvents.pipe(
            first((event) => event.type === CashierEventType.FundsAdded),
            map((event) => event as FundsAdded),
        );
    }

    getUrl(): string {
        const resolvedPath = this.quickDepositConfig.path
            .replace('{USERID}', this.userService.id || '')
            .replace('{PRODUCTID}', this.appInfoConfig.product)
            .replace('{BRANDID}', this.appInfoConfig.brand)
            .replace('{CHANNELID}', this.appInfoConfig.channel)
            .replace('{LANG}', this.page.lang)
            .replace('{SSO}', this.userService.ssoToken || '')
            .replace('{STAKEINCENTS}', this.stakeInCents.toString())
            .replace('{WALLETBUCKETTYPE}', this.mapWalletBucketType(this.accountBalanceService.selectedWallet));

        return `${this.quickDepositConfig.url}${resolvedPath}`;
    }

    setCashierWindow(cashierWindow: Window): void {
        this.cashierWindow = cashierWindow;
    }

    handleCashierMessage = (event: MessageEvent) => {
        if (!event.data.eventName) {
            return;
        }

        switch (event.data.eventName.toLowerCase()) {
            case CashierEventType.DepositAvailable.toLowerCase():
                return this.onDepositAvailableMessage(event);
            case CashierEventType.SelectedPaymentMethod.toLowerCase():
                return this.onSelectedPaymentMethodMessage(event);
            case CashierEventType.FundsAdded.toLowerCase():
                return this.onFundsAddedMessage(event);
            case CashierEventType.WindowResize.toLowerCase():
                return this.onWindowResizeMessage(event);
            case CashierEventType.WindowScroll.toLowerCase():
                return this.onWindowScrollMessage(event);
            case CashierEventType.CashierPage.toLowerCase():
                return this.onCashierPageMessage(event);
            case CashierEventType.ClosePage.toLowerCase():
                return this.onCancelPageMessage(event);
            case CashierEventType.MessageToNative.toLowerCase():
                return this.onMessageToNative(event);
        }
    };

    get isQuickDepositLoaderEnabled$(): Observable<boolean> {
        return this.isQuickDepositLoaderEnabledSubject$;
    }

    get currentStake(): number {
        return this.stake;
    }

    private mapWalletBucketType(selectedWallet: WalletItem | undefined): string {
        if (!selectedWallet) {
            return 'ALL';
        }
        switch (selectedWallet.type) {
            case WalletType.Any:
                return 'ALL';
            case WalletType.Main:
                return 'NONPAYPAL';
            case WalletType.PayPal:
                return 'PAYPAL';
        }
    }

    private onDepositAvailableMessage(event: MessageEvent): void {
        this.isAvailable = event.data.eventData['available'];
        this.dispatchThenEmitEvent({
            type: CashierEventType.DepositAvailable,
            available: this.isAvailable,
        } as DepositAvailable);
    }

    private onSelectedPaymentMethodMessage(event: MessageEvent): void {
        this.dispatchThenEmitEvent({
            type: CashierEventType.SelectedPaymentMethod,
            paymentMethodName: this.mapPaymentMethod(event.data.eventData['paymentMethodName']),
        } as SelectedPaymentMethod);
    }

    private onFundsAddedMessage(event: MessageEvent): void {
        const success = event.data.eventData['success'];
        this.isAvailable = success;

        const fundsAdded = {
            type: CashierEventType.FundsAdded,
            success: this.isAvailable,
            amount: event.data.eventData['amount'],
            quickJourney: event.data.eventData['quickJourney'],
        } as FundsAdded;

        if (!success) {
            this.dispatchThenEmitEvent(fundsAdded);

            return;
        }

        this.accountBalanceService
            .refresh$()
            .pipe(
                catchError((error) => {
                    this.logger.error(error, 'Failed updating user balance from quick deposit service.');

                    return of(null);
                }),
            )
            .subscribe(() => {
                this.dispatchThenEmitEvent(fundsAdded);
                if (!this.keepOpen) {
                    this.close(true);
                }
            });
    }

    private onWindowResizeMessage(event: MessageEvent): void {
        this.dispatchThenEmitEvent({
            type: CashierEventType.WindowResize,
            height: event.data.eventData['height'],
        } as WindowResize);
    }

    private onWindowScrollMessage(event: MessageEvent): void {
        this.dispatchThenEmitEvent({
            type: CashierEventType.WindowScroll,
            deltaY: event.data.eventData['deltaY'],
        } as WindowScroll);
    }

    private onCashierPageMessage(event: MessageEvent): void {
        this.dispatchThenEmitEvent({
            type: CashierEventType.CashierPage,
            name: this.mapDepositType(event.data.eventData['name']),
        } as CashierPage);
    }

    private onCancelPageMessage(event: MessageEvent): void {
        this.close(event.data.eventData['isClose']);
    }

    private onMessageToNative(event: MessageEvent): void {
        this.nativeAppService.sendToNative(event.data.eventData);
    }

    private dispatchThenEmitEvent(event: CashierWindowEvent): void {
        this.dispatchCashierWindowEvent(event);
        this.cashierEvents.next(event);
    }

    private dispatchCashierWindowEvent(event: CashierWindowEvent): void {
        switch (event.type) {
            case CashierEventType.WindowScroll:
                this.store.dispatch(QuickDepositActions.windowScroll({ payload: event as WindowScroll }));
                break;
            case CashierEventType.FundsAdded:
                this.store.dispatch(QuickDepositActions.fundsAdded({ payload: event as FundsAdded }));
                break;
            case CashierEventType.WindowResize:
                this.store.dispatch(QuickDepositActions.windowResize({ payload: event as WindowResize }));
                break;
            case CashierEventType.DepositAvailable:
                this.store.dispatch(QuickDepositActions.depositAvailable({ payload: event as DepositAvailable }));
                break;
            case CashierEventType.CashierPage:
                this.store.dispatch(QuickDepositActions.cashierPage({ payload: event as CashierPage }));
                break;
            case CashierEventType.SelectedPaymentMethod:
                this.store.dispatch(QuickDepositActions.selectedPaymentMethod({ payload: event as SelectedPaymentMethod }));
                break;
        }
    }

    private mapPaymentMethod(value: string): PaymentMethod | undefined {
        switch (value.toLowerCase()) {
            case PaymentMethod.GooglePay.toLowerCase():
                return PaymentMethod.GooglePay;
            case PaymentMethod.ApplePayDirect.toLowerCase():
                return PaymentMethod.ApplePayDirect;
        }

        return;
    }

    private get stakeInCents(): number {
        return this.stake * 100;
    }

    private mapDepositType(value: string): QuickDepositPage {
        switch (value.toLowerCase()) {
            case QuickDepositPage.Deposit.toLowerCase():
                return QuickDepositPage.Deposit;
            case QuickDepositPage.Withdrawal.toLowerCase():
                return QuickDepositPage.Withdrawal;
            default:
                throw new Error(`QuickDeposit type ${value} not supported`);
        }
    }

    isQuickDepositEnabled(): Observable<boolean> {
        this.toggleQuickDepositLoader(true);

        if (this.quickDepositAllowedResult.isConfigReady) {
            this.toggleQuickDepositLoader(false);

            return of(this.quickDepositAllowedResult.isQuickDepositAllowed);
        }

        return this.quickDepositAllowedResult.whenReady.pipe(map(() => this.quickDepositAllowedResult.isQuickDepositAllowed));
    }

    disableQuickDepositLoader(): void {
        this.toggleQuickDepositLoader(false);
    }

    private toggleQuickDepositLoader(state: boolean): void {
        this.isQuickDepositLoaderEnabledSubject$.next(state);
    }
}
